import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import './AccountPages.css';

function clampRating(value) {
  const rating = Number(value);
  if (!Number.isFinite(rating)) return 0;
  return Math.max(0, Math.min(5, Math.round(rating)));
}

function formatDate(value, language) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US');
}

function normalizeReview(review, language) {
  return {
    id: review.id,
    name: review.name || review.reviewer_name || 'Anonymous',
    role: review.role || 'user',
    rating: clampRating(review.rating),
    content: review.content || review.comment || '',
    time: review.time || formatDate(review.created_at, language),
    taskTitle: review.task_title || '',
  };
}

export default function Reviews() {
  const { language } = useLanguage();
  const isZh = language === 'zh';
  const [liveReviews, setLiveReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ avg_rating: null, total: 0, positive_rate: 0 });
  const text = isZh ? {
    title: '评价',
    subtitle: '展示服务态度、专业程度、结果满意度等评分内容。',
    avg: '平均评分',
    count: '评价数量',
    positive: '好评率',
    empty: '暂无真实评价。完成订单并评价后会显示在这里。',
    roles: { client: '客户', freelancer: '自由职业者', user: '用户' },
  } : {
    title: 'Reviews',
    subtitle: 'Service attitude, professionalism, and delivery satisfaction in one place.',
    avg: 'Average rating',
    count: 'Reviews',
    positive: 'Positive rate',
    empty: 'No verified reviews yet. Completed orders with reviews will appear here.',
    roles: { client: 'Client', freelancer: 'Freelancer', user: 'User' },
  };

  useEffect(() => {
    let mounted = true;

    api.get('/reviews', { params: { limit: 12 } })
      .then(({ data }) => {
        if (!mounted) return;
        const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
        setLiveReviews(reviews);
        setReviewStats({
          avg_rating: data?.avg_rating ?? null,
          total: Number(data?.total || reviews.length),
          positive_rate: Number(data?.positive_rate || 0),
        });
      })
      .catch(() => {
        if (mounted) setLiveReviews([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const displayReviews = useMemo(() => {
    return liveReviews.map(review => normalizeReview(review, language));
  }, [language, liveReviews]);
  const averageRating = reviewStats.avg_rating == null ? '-' : Number(reviewStats.avg_rating).toFixed(1);
  const reviewCount = reviewStats.total || displayReviews.length;
  const positiveRate = Number(reviewStats.positive_rate || 0);

  return (
    <div className="account-page">
      <div className="container">
        <div className="account-head">
          <div>
            <h1>{text.title}</h1>
            <p>{text.subtitle}</p>
          </div>
        </div>
        <div className="account-grid">
          <div className="metric-card"><span>{text.avg}</span><strong>{averageRating}</strong></div>
          <div className="metric-card"><span>{text.count}</span><strong>{reviewCount}</strong></div>
          <div className="metric-card"><span>{text.positive}</span><strong>{positiveRate}%</strong></div>
        </div>
        {displayReviews.length === 0 && <div className="account-card"><p className="card-muted">{text.empty}</p></div>}
        {displayReviews.map(r => (
          <div className="account-card" key={r.id}>
            <h2>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</h2>
            <p className="card-muted">{r.content}</p>
            <p className="card-muted" style={{ marginTop: 10 }}>
              {r.name} · {text.roles[r.role] || r.role} · {r.time}
              {r.taskTitle ? ` · ${r.taskTitle}` : ''}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
